import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Split, Stack} from '@dropbox/dig-foundations';
import {Employee} from 'client';
import {Avatar} from 'components/DSYS/Avatar';

import {PersonCard} from './PeopleCards';
import {prefetchProfile} from './profiles';
import styles from './SearchBox.module.css';
import {Label, Meta, Row, RowList} from './Typography';

/**
 * People in the pane as full rows (face, name, role), like a meeting's People
 * and the profile page's Peers. `chain` joins the faces with a thin line, top
 * to bottom, for a reporting line: their manager first, then up. `grid` lays
 * them out two-up as the top row's cards instead, for groups with no order.
 */
export const PeopleRows = ({
  label,
  people,
  onPick,
  chain = false,
  grid = false,
}: {
  label: string;
  people: Employee[];
  onPick: (person: Employee) => void;
  chain?: boolean;
  grid?: boolean;
}) =>
  people.length > 0 ? (
    <Stack gap="12">
      <Label>{label}</Label>
      {grid ? (
        <div className={styles.cardGrid}>
          {people.map((person) => (
            <PersonCard key={person.ldap} person={person} onOpen={onPick} />
          ))}
        </div>
      ) : (
        <PeopleList people={people} onPick={onPick} chain={chain} />
      )}
    </Stack>
  ) : null;

const PeopleList = ({
  people,
  onPick,
  chain,
}: {
  people: Employee[];
  onPick: (person: Employee) => void;
  chain: boolean;
}) => (
  <RowList>
    {people.map((person) => (
      <div key={person.ldap} className={chain ? styles.chainLink : undefined}>
        <Row onClick={() => onPick(person)} onMouseOver={() => prefetchProfile(person)}>
          <Split gap="12" alignY="center">
            <Split.Item className={styles.chainFace}>
              <Avatar user={person} size="medium" />
            </Split.Item>
            <Split.Item width="fill" style={{minWidth: 0}}>
              <Text tagName="div">{person.name}</Text>
              {person.role && <Meta>{person.role}</Meta>}
            </Split.Item>
          </Split>
        </Row>
      </div>
    ))}
  </RowList>
);

/** While the profile loads: the same sections, in the same places, as faint rows. */
export const PeopleRowsSkeleton = () => (
  <Stack gap="44">
    <Stack gap="12">
      <Skeleton.Text width={72} size="small" />
      <Split gap="12" alignY="center">
        <Split.Item>
          <Skeleton.Avatar size="medium" />
        </Split.Item>
        <Split.Item width="fill">
          <Skeleton.Text width={140} />
          <Skeleton.Text width={200} size="small" />
        </Split.Item>
      </Split>
    </Stack>
    <Stack gap="12">
      <Skeleton.Text width={72} size="small" />
      <div className={styles.cardGrid}>
        {Array.from({length: 4}, (_, index) => (
          <Skeleton.Box key={index} height={56} width={330} withBorderRadius="medium" />
        ))}
      </div>
    </Stack>
  </Stack>
);
